import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { FileText, Clock, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Progress } from "../components/ui/progress";
import api from "../lib/api";

export default function CourseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [done, setDone] = useState(() => { try { return JSON.parse(localStorage.getItem(`eon_course_${id}`) || "[]"); } catch { return []; } });

  useEffect(() => { api.get(`/courses/${id}`).then((r) => setC(r.data)); }, [id]);

  const locked = false;

  const toggle = (lid) => {
    const next = done.includes(lid) ? done.filter((x) => x !== lid) : [...done, lid];
    setDone(next); localStorage.setItem(`eon_course_${id}`, JSON.stringify(next));
    if (c && next.length === c.lessons.length) toast.success("Course complete! Certificate unlocked 🎓".replace("🎓",""));
  };

  if (!c) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Loading...</div></Layout>;
  const pct = Math.round((done.length / c.lessons.length) * 100);

  return (
    <Layout>
      <section className="relative h-[40vh] overflow-hidden">
        <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-eon-bg to-eon-bg/40" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 h-full flex flex-col justify-end pb-10">
          <Link to="/academy" className="flex items-center gap-2 text-sm text-eon-text/70 hover:text-eon-gold mb-4 transition-colors"><ArrowLeft size={16} /> Academy</Link>
          <div className="eon-label mb-2">{c.level} · {c.instructor}</div>
          <h1 className="eon-heading text-4xl lg:text-6xl uppercase max-w-3xl">{c.title}</h1>
        </div>
      </section>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <p className="text-lg text-eon-text/80 leading-relaxed">{c.summary}</p>
          <div className="mt-10">
            <div className="flex items-center justify-between mb-4"><h2 className="eon-heading text-2xl uppercase">Lessons</h2><span className="eon-label">{c.lessons.length} lessons · {c.duration}</span></div>
            <div className="border border-eon-border divide-y divide-eon-border rounded-lg overflow-hidden">
              {c.lessons.map((l, i) => (
                <details key={l.id} className="group" data-testid={`lesson-${l.id}`}>
                  <summary className="w-full flex items-center gap-4 p-4 hover:bg-eon-surface/60 transition-colors cursor-pointer list-none">
                    <span className="font-mono text-eon-muted text-sm w-6">{String(i + 1).padStart(2, "0")}</span>
                    {done.includes(l.id) ? <CheckCircle2 size={20} className="text-eon-gold" /> : <FileText size={20} className="text-eon-muted" />}
                    <span className="flex-1 text-eon-text font-medium">{l.title}</span>
                    <span className="text-xs text-eon-muted flex items-center gap-1"><Clock size={12} /> {l.duration}</span>
                    <span className="text-eon-gold text-lg leading-none group-open:rotate-45 transition-transform ml-2">+</span>
                  </summary>
                  <div className="px-4 pb-5 pt-1 sm:pl-14">
                    <div className="text-sm text-eon-text/85 leading-relaxed whitespace-pre-line">{l.content || "Lesson content coming soon."}</div>
                    <button onClick={() => toggle(l.id)} data-testid={`lesson-complete-${l.id}`}
                      className={`mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-full transition-colors ${done.includes(l.id) ? "bg-eon-gold text-white" : "border border-eon-border text-eon-text hover:border-eon-gold"}`}>
                      <CheckCircle2 size={14} /> {done.includes(l.id) ? "Completed" : "Mark Complete"}
                    </button>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
        <aside>
          <div className="sticky top-24 eon-card p-6">
            <div className="eon-label mb-2">Your Progress</div>
            <div className="font-display text-4xl font-black text-eon-gold">{pct}%</div>
            <Progress value={pct} className="mt-4 h-2" />
            <div className="text-sm text-eon-muted mt-3">{done.length} of {c.lessons.length} lessons complete</div>
            {pct === 100 && <div className="mt-5 border border-eon-gold text-eon-gold text-center py-3 text-xs font-bold uppercase tracking-wider">Certificate Earned</div>}
            <p className="text-xs text-eon-muted mt-6 leading-relaxed border-t border-eon-border pt-4">Educational content only. Always consult a professional for behavioral or medical concerns.</p>
          </div>
        </aside>
      </div>
    </Layout>
  );
}
